<script>
  import { supabase } from '../../lib/supabase.js';
  import { showToast } from '../../lib/stores/toast.js';

  let { pageId, workspaceId, onclose } = $props();

  let existing = $state([]);
  let newButtons = $state([{ name: '', description: '' }]);
  let saving = $state(false);
  let removeStep = $state(0);
  let removeTarget = $state(null);
  let removeAction = $state(null);
  let editingDescId = $state(null);
  let descDraft = $state('');

  async function load() {
    const { data } = await supabase
      .from('bb_tech_buttons')
      .select('id, name, is_hidden, description')
      .eq('page_id', pageId)
      .is('deleted_at', null)
      .order('sort_order', { ascending: true });
    existing = data || [];
  }

  function startEditDesc(btn) {
    editingDescId = btn.id;
    descDraft = btn.description || '';
  }

  async function saveDesc() {
    if (!editingDescId) return;
    await supabase.from('bb_tech_buttons').update({ description: descDraft.trim() || null }).eq('id', editingDescId);
    editingDescId = null;
    descDraft = '';
    await load();
    showToast('Saved', 'success');
  }

  function onKey(e, idx) {
    if (e.key === 'Enter') {
      e.preventDefault();
      newButtons = [...newButtons.slice(0, idx + 1), { name: '', description: '' }, ...newButtons.slice(idx + 1)];
      setTimeout(() => document.querySelector(`[data-btn="${idx + 1}"]`)?.focus(), 0);
    } else if (e.key === 'Backspace' && newButtons[idx].name === '' && newButtons.length > 1) {
      e.preventDefault();
      newButtons = newButtons.filter((_, i) => i !== idx);
    }
  }

  async function save() {
    saving = true;
    try {
      const clean = newButtons.filter(b => b.name.trim()).map((b, i) => ({
        workspace_id: workspaceId,
        page_id: pageId,
        name: b.name.trim(),
        description: b.description.trim() || null,
        sort_order: existing.length + i
      }));
      if (clean.length > 0) {
        const { error } = await supabase.from('bb_tech_buttons').insert(clean);
        if (error) throw error;
      }
      showToast('Buttons saved', 'success');
      onclose?.();
    } catch (e) {
      showToast('Save failed: ' + e.message, 'error');
    } finally { saving = false; }
  }

  function startRemove(btn, action) {
    removeTarget = btn;
    removeAction = action;
    removeStep = 1;
  }

  async function confirmRemoveStep() {
    if (removeStep === 1) { removeStep = 2; return; }
    if (removeStep === 2) { removeStep = 3; return; }
    if (removeStep === 3) {
      try {
        if (removeAction === 'delete') {
          await supabase.from('bb_tech_choices').update({ deleted_at: new Date().toISOString() }).eq('source_button_id', removeTarget.id);
          await supabase.from('bb_tech_buttons').update({ deleted_at: new Date().toISOString() }).eq('id', removeTarget.id);
        } else {
          await supabase.from('bb_tech_buttons').update({ is_hidden: true }).eq('id', removeTarget.id);
        }
        showToast(removeAction === 'delete' ? 'Button removed' : 'Button hidden', 'success');
        await load();
      } catch (e) {
        showToast('Remove failed: ' + e.message, 'error');
      }
      removeStep = 0;
      removeTarget = null;
      removeAction = null;
    }
  }

  function cancelRemove() {
    removeStep = 0;
    removeTarget = null;
    removeAction = null;
  }

  $effect(() => { load(); });
</script>

<div class="pp-overlay" onclick={onclose}>
  <div class="pp-modal" onclick={(e) => e.stopPropagation()}>
    <header class="pp-header">
      <h4>Buttons</h4>
      <button class="pp-x" onclick={onclose} type="button">×</button>
    </header>
    <div class="pp-body">
      {#if existing.length > 0}
        <div class="bp-existing-section">
          <div class="bp-section-label">On this page</div>
          {#each existing as b}
            <div class="bp-existing-btn-wrap">
              <div class="bp-existing-btn" class:hidden={b.is_hidden}>
                <div class="bp-btn-info">
                  <span class="bp-btn-name">{b.name}{b.is_hidden ? ' (hidden)' : ''}</span>
                  {#if b.description}
                    <span class="bp-btn-desc">{b.description}</span>
                  {/if}
                </div>
                <div class="bp-btn-actions">
                  <button class="bp-edit-btn" onclick={() => startEditDesc(b)} type="button" title="Edit description">✎</button>
                  <button class="bp-x-btn" onclick={() => startRemove(b, 'delete')} type="button" title="Remove from all pages">×</button>
                </div>
              </div>
              {#if editingDescId === b.id}
                <div class="bp-desc-edit">
                  <input
                    class="bp-desc-input"
                    bind:value={descDraft}
                    placeholder="What does this button do? (shown on hover)"
                    onkeydown={(e) => { if (e.key === 'Enter') { e.preventDefault(); saveDesc(); } if (e.key === 'Escape') editingDescId = null; }}
                  />
                  <button class="pp-secondary" onclick={() => editingDescId = null} type="button">Cancel</button>
                  <button class="pp-primary" onclick={saveDesc} type="button">Save</button>
                </div>
              {/if}
            </div>
          {/each}
        </div>
      {/if}

      <div class="bp-section-label">Add new</div>
      {#each newButtons as btn, idx}
        <div class="bp-new-row">
          <input
            class="pp-step bp-name-input"
            data-btn={idx}
            bind:value={newButtons[idx].name}
            onkeydown={(e) => onKey(e, idx)}
            placeholder="Button name"
          />
          <input
            class="pp-step bp-desc-input-new"
            bind:value={newButtons[idx].description}
            placeholder="What it does (optional)"
          />
        </div>
      {/each}
    </div>

    {#if removeStep > 0}
      <div class="bp-confirm">
        {#if removeStep === 1}
          <p>This removes <strong>{removeTarget.name}</strong> from every page that uses it.</p>
          <div class="bp-confirm-actions">
            <button class="pp-secondary" onclick={cancelRemove} type="button">Cancel</button>
            <button class="pp-secondary" onclick={() => { removeAction = 'hide'; removeStep = 2; }} type="button">Hide instead</button>
            <button class="pp-primary" onclick={confirmRemoveStep} type="button">Delete</button>
          </div>
        {:else if removeStep === 2}
          <p>Are you sure?</p>
          <div class="bp-confirm-actions">
            <button class="pp-secondary" onclick={cancelRemove} type="button">Cancel</button>
            <button class="pp-primary" onclick={confirmRemoveStep} type="button">Yes</button>
          </div>
        {:else if removeStep === 3}
          <p>Any choice connected to this button will be removed too. Confirm?</p>
          <div class="bp-confirm-actions">
            <button class="pp-secondary" onclick={cancelRemove} type="button">Cancel</button>
            <button class="pp-primary" onclick={confirmRemoveStep} type="button">Confirm</button>
          </div>
        {/if}
      </div>
    {/if}

    <footer class="pp-footer">
      <button class="pp-secondary" onclick={onclose} type="button">Cancel</button>
      <button class="pp-primary" onclick={save} disabled={saving} type="button">{saving ? 'Saving…' : 'Done'}</button>
    </footer>
  </div>
</div>

<style>
  .pp-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 250; }
  .pp-modal { background: #0a0a0c; border: 1px solid rgba(212,175,55,0.3); border-radius: 12px; width: 480px; max-width: 95vw; max-height: 80vh; display: flex; flex-direction: column; }
  .pp-header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .pp-header h4 { margin: 0; font-family: 'DM Serif Display', serif; font-size: 16px; color: #f0efe9; }
  .pp-x { background: rgba(255,255,255,0.06); color: #b8b8bc; border: 1px solid rgba(255,255,255,0.1); width: 26px; height: 26px; border-radius: 50%; cursor: pointer; font-size: 14px; }
  .pp-body { padding: 14px 16px; overflow-y: auto; display: flex; flex-direction: column; gap: 6px; }
  .bp-section-label { font-family: 'JetBrains Mono', monospace; font-size: 9px; letter-spacing: 1.4px; text-transform: uppercase; color: #9aa0a6; margin-top: 8px; }
  .bp-existing-section { display: flex; flex-direction: column; gap: 4px; }
  .bp-existing-btn { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 6px; color: #d8d8dc; font-size: 12px; }
  .bp-existing-btn.hidden { opacity: 0.5; }
  .bp-x-btn { background: rgba(255,255,255,0.04); color: #9aa0a6; border: 1px solid rgba(255,255,255,0.08); width: 20px; height: 20px; border-radius: 50%; cursor: pointer; font-size: 11px; line-height: 1; padding: 0; }
  .bp-x-btn:hover { background: rgba(220,100,100,0.15); color: #d48484; }
  .pp-step { background: rgba(0,0,0,0.4); border: 1px solid rgba(255,255,255,0.1); color: #f4f4f6; padding: 8px 10px; border-radius: 6px; font-size: 13px; font-family: inherit; }
  .pp-step:focus { outline: none; border-color: rgba(212,175,55,0.4); }
  .bp-confirm { padding: 12px 16px; background: rgba(220,100,100,0.08); border-top: 1px solid rgba(220,100,100,0.2); }
  .bp-confirm p { margin: 0 0 10px; color: #f0efe9; font-size: 13px; }
  .bp-confirm-actions { display: flex; gap: 6px; justify-content: flex-end; }
  .pp-footer { display: flex; gap: 8px; justify-content: flex-end; padding: 10px 16px; border-top: 1px solid rgba(255,255,255,0.06); }
  .pp-primary { background: linear-gradient(180deg, #d4af37, #b8941f); color: #0a0a0c; border: none; padding: 7px 14px; border-radius: 6px; cursor: pointer; font-weight: 600; font-size: 12px; }
  .pp-secondary { background: rgba(255,255,255,0.05); color: #d8d8dc; border: 1px solid rgba(255,255,255,0.1); padding: 7px 12px; border-radius: 6px; cursor: pointer; font-size: 12px; }
  .bp-existing-btn-wrap { display: flex; flex-direction: column; gap: 2px; }
  .bp-btn-info { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
  .bp-btn-name { color: #d8d8dc; font-size: 12px; }
  .bp-btn-desc { color: #9aa0a6; font-size: 10px; font-style: italic; }
  .bp-btn-actions { display: flex; gap: 4px; align-items: center; }
  .bp-edit-btn {
    background: rgba(255,255,255,0.04); color: #9aa0a6;
    border: 1px solid rgba(255,255,255,0.08);
    width: 20px; height: 20px; border-radius: 50%;
    cursor: pointer; font-size: 10px; line-height: 1; padding: 0;
  }
  .bp-edit-btn:hover { background: rgba(212,175,55,0.15); color: #d4af37; }
  .bp-desc-edit {
    display: flex; gap: 4px; padding: 4px 0;
  }
  .bp-desc-input {
    flex: 1;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(212,175,55,0.3);
    color: #f4f4f6;
    padding: 6px 8px; border-radius: 6px;
    font-size: 11px;
  }
  .bp-new-row { display: flex; flex-direction: column; gap: 4px; padding: 4px 0; border-bottom: 1px dashed rgba(255,255,255,0.04); }
  .bp-name-input { font-weight: 500; }
  .bp-desc-input-new { font-size: 11px; color: #b8b8bc; }
</style>
